import { api } from '@/lib/api';
import type {
  CustomActionResponse,
  CreateCustomActionRequest,
  UpdateCustomActionRequest,
  CustomActionQuery,
  PaginatedResponse,
} from '@troia-v3/types';

/**
 * Custom Actions API Service
 * Handles all HTTP requests related to custom actions
 */
export const customActionsApi = {
  /**
   * Get all custom actions with pagination
   */
  async getAll(query?: CustomActionQuery): Promise<PaginatedResponse<CustomActionResponse>> {
    const response = await api.get('custom-actions', { params: query });
    return response.data;
  },

  /**
   * Get custom action by ID
   */
  async getById(id: string): Promise<CustomActionResponse> {
    const response = await api.get(`custom-actions/${id}`);
    return response.data;
  },

  /**
   * Create new custom action
   */
  async create(data: CreateCustomActionRequest): Promise<CustomActionResponse> {
    const response = await api.post('custom-actions', data);
    return response.data;
  },

  /**
   * Update custom action
   */
  async update(id: string, data: UpdateCustomActionRequest): Promise<CustomActionResponse> {
    const response = await api.patch(`custom-actions/${id}`, data);
    return response.data;
  },

  /**
   * Delete custom action
   */
  async delete(id: string): Promise<void> {
    await api.delete(`custom-actions/${id}`);
  },

  /**
   * Update custom action status
   */
  async updateStatus(id: string, status: 'active' | 'inactive'): Promise<CustomActionResponse> {
    const response = await api.patch(`custom-actions/${id}/status`, { status });
    return response.data;
  },

  /**
   * Test custom action execution
   */
  async test(id: string, parameters: Record<string, any>): Promise<any> {
    const response = await api.post(`custom-actions/${id}/test`, { parameters });
    return response.data;
  },

  /**
   * Get execution logs for a custom action
   */
  async getLogs(customActionId: string, query?: Record<string, unknown>) {
    const response = await api.get('custom-action-logs', {
      params: { customActionId, ...query },
    });
    return response.data;
  },

  /**
   * Get execution stats for a custom action
   */
  async getLogStats(customActionId: string) {
    const response = await api.get(`custom-action-logs/${customActionId}/stats`);
    return response.data;
  },
};
